import type { ApiGatewayPermissionFamily } from './api-gateway-management';

const LEGACY_VIEW_PERMISSION = 'tenant.api-gateway.legacy.view';
const LEGACY_MANAGE_PERMISSION = 'tenant.api-gateway.legacy.manage';

const normalize = (value: string) => value.trim().toLowerCase();

export interface ApiGatewayPermissionSummary {
  permissions: string[];
  labels: string[];
  summary: string;
}

export function normalizePermissionKeys(values?: string[] | null): string[] {
  if (!values?.length) {
    return [];
  }

  const seen = new Set<string>();
  const normalized: string[] = [];

  for (const value of values) {
    const next = normalize(value);
    if (!next || seen.has(next)) {
      continue;
    }
    seen.add(next);
    normalized.push(next);
  }

  return normalized;
}

function legacyPermissionsFromScopes(scopes?: string[] | null): string[] {
  const normalized = normalizePermissionKeys(scopes);
  if (normalized.includes('legacy.write')) {
    return [LEGACY_MANAGE_PERMISSION];
  }
  if (normalized.includes('legacy.read')) {
    return [LEGACY_VIEW_PERMISSION];
  }
  return [];
}

export function summarizeApiGatewayKeyPermissions(
  permissions: string[] | undefined,
  scopes: string[] | undefined,
  families: ApiGatewayPermissionFamily[]
): ApiGatewayPermissionSummary {
  const normalizedPermissions = normalizePermissionKeys(permissions);
  const effectivePermissions = normalizedPermissions.length > 0 ? normalizedPermissions : legacyPermissionsFromScopes(scopes);

  if (effectivePermissions.length === 0) {
    return {
      permissions: [],
      labels: [],
      summary: 'No assigned permissions',
    };
  }

  const permissionDisplay = new Map<string, { label: string; access: 'View' | 'Manage'; order: number }>();

  families.forEach((family, index) => {
    permissionDisplay.set(normalize(family.read_permission), {
      label: family.label,
      access: 'View',
      order: index * 2,
    });
    permissionDisplay.set(normalize(family.manage_permission), {
      label: family.label,
      access: 'Manage',
      order: index * 2 + 1,
    });
  });

  const labels = effectivePermissions
    .map(permission => {
      const display = permissionDisplay.get(permission);
      if (!display) {
        return { label: permission, order: Number.MAX_SAFE_INTEGER };
      }
      return { label: `${display.label} (${display.access})`, order: display.order };
    })
    .sort((left, right) => left.order - right.order)
    .map(entry => entry.label);

  return {
    permissions: effectivePermissions,
    labels,
    summary: labels.join(', '),
  };
}
